import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { IoMdTrophy, IoMdAdd, IoMdTrash } from "react-icons/io";
import { MdEdit, MdClose } from "react-icons/md";

const CATEGORY_OPTIONS = [
  { value: "oneToOneCalls", label: "1-1 Calls" },
  { value: "gamesPlayed", label: "Games Played" },
  { value: "gangOnlineHours", label: "Gang Online Hours" },
  { value: "mastiHosted", label: "Masti Hosted" },
  { value: "mastiJoined", label: "Masti Joined" },
  { value: "giftsSent", label: "Gifts Sent" },
  { value: "giftsReceived", label: "Gifts Received" },
  { value: "groupCallsJoined", label: "Group Calls Joined" },
  { value: "uniqueUsersCalled", label: "Unique Users Called" },
  { value: "loginSessions", label: "Login Sessions" },
];

const RESET_PERIODS = ["daily", "weekly", "monthly", "lifetime"];
const REWARD_TYPES = ["none", "coins", "badge"];

const MilestoneConfigPage = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  // Form state
  const [formCategory, setFormCategory] = useState("");
  const [formDisplayName, setFormDisplayName] = useState("");
  const [formResetPeriod, setFormResetPeriod] = useState("monthly");
  const [formIsActive, setFormIsActive] = useState(true);
  const [formTiers, setFormTiers] = useState([]);

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/user-activity/milestone-configs`
      );
      setConfigs(res.data?.configs || []);
    } catch (error) {
      console.error("Error fetching milestone configs:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const resetForm = () => {
    setFormCategory("");
    setFormDisplayName("");
    setFormResetPeriod("monthly");
    setFormIsActive(true);
    setFormTiers([]);
    setEditingConfig(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (config) => {
    setEditingConfig(config);
    setFormCategory(config.category);
    setFormDisplayName(config.displayName);
    setFormResetPeriod(config.resetPeriod);
    setFormIsActive(config.isActive);
    setFormTiers(config.tiers || []);
    setShowModal(true);
  };

  const addTier = () => {
    const nextTierNum = formTiers.length > 0 ? Math.max(...formTiers.map((t) => t.tier)) + 1 : 1;
    setFormTiers([
      ...formTiers,
      { tier: nextTierNum, threshold: 10, rewardType: "none", rewardAmount: 0, badgeName: "" },
    ]);
  };

  const updateTier = (index, field, value) => {
    const updated = [...formTiers];
    updated[index] = { ...updated[index], [field]: value };
    setFormTiers(updated);
  };

  const removeTier = (index) => {
    setFormTiers(formTiers.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formCategory || !formDisplayName) {
      alert("Category and Display Name are required");
      return;
    }

    const payload = {
      category: formCategory,
      displayName: formDisplayName,
      resetPeriod: formResetPeriod,
      isActive: formIsActive,
      tiers: formTiers,
      adminId: "admin",
    };

    try {
      if (editingConfig) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/user-activity/milestone-configs/${editingConfig._id}`,
          payload
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/user-activity/milestone-configs`,
          payload
        );
      }
      setShowModal(false);
      resetForm();
      fetchConfigs();
    } catch (error) {
      console.error("Error saving config:", error);
      alert(error.response?.data?.message || "Failed to save");
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm("Are you sure? This will also remove all granted milestones for this category.")) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/user-activity/milestone-configs/${configId}`
      );
      fetchConfigs();
    } catch (error) {
      console.error("Error deleting config:", error);
      alert("Failed to delete");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <IoMdTrophy className="text-3xl text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Milestone Configuration</h1>
            <p className="text-sm text-gray-500">
              Define reward milestones for user activity tracking
            </p>
          </div>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 rounded-xl bg-[#c51369] px-5 py-2.5 text-sm font-medium text-white shadow-md hover:bg-pink-700 transition-colors"
        >
          <IoMdAdd className="text-lg" />
          Create Milestone
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-200 border-t-[#c51369]"></div>
        </div>
      ) : configs.length === 0 ? (
        <div className="rounded-2xl bg-white py-20 text-center shadow-sm">
          <IoMdTrophy className="mx-auto mb-3 text-6xl text-gray-200" />
          <p className="text-lg font-medium text-gray-500">No milestones configured yet</p>
          <p className="mt-1 text-sm text-gray-400">
            Click "Create Milestone" to define your first reward category
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {configs.map((config) => (
            <div key={config._id} className="rounded-2xl bg-white p-5 shadow-sm border border-gray-100">
              {/* Config Header */}
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{config.displayName}</h3>
                  <p className="text-xs text-gray-400 font-mono">{config.category}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      config.isActive
                        ? "bg-green-100 text-green-600"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {config.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              {/* Reset Period */}
              <div className="mb-3 flex items-center gap-2">
                <span className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-600 font-medium">
                  Reset: {config.resetPeriod}
                </span>
                <span className="rounded bg-purple-50 px-2 py-0.5 text-xs text-purple-600 font-medium">
                  {config.tiers?.length || 0} tier{(config.tiers?.length || 0) !== 1 ? "s" : ""}
                </span>
              </div>

              {/* Tiers Preview */}
              {config.tiers?.length > 0 && (
                <div className="mb-4 space-y-1.5">
                  {config.tiers.sort((a, b) => a.tier - b.tier).map((tier) => (
                    <div
                      key={tier.tier}
                      className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm"
                    >
                      <span className="text-gray-600">
                        Tier {tier.tier}: <span className="font-medium text-gray-800">{tier.threshold}</span>
                      </span>
                      <span className="text-xs text-gray-400">
                        {tier.rewardType === "coins"
                          ? `${tier.rewardAmount} coins`
                          : tier.rewardType === "badge"
                          ? tier.badgeName || "Badge"
                          : "No reward"}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 border-t border-gray-100 pt-3">
                <button
                  onClick={() => openEditModal(config)}
                  className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-gray-100 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  <MdEdit /> Edit
                </button>
                <button
                  onClick={() => handleDelete(config._id)}
                  className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-red-50 py-2 text-sm font-medium text-red-600 hover:bg-red-100 transition-colors"
                >
                  <IoMdTrash /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Modal ── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">
                {editingConfig ? "Edit Milestone" : "Create Milestone"}
              </h2>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="rounded-full p-1 hover:bg-gray-100"
              >
                <MdClose className="text-xl text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Category */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
                {editingConfig ? (
                  <input
                    value={formCategory}
                    readOnly
                    className="w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm"
                  />
                ) : (
                  <select
                    value={formCategory}
                    onChange={(e) => {
                      setFormCategory(e.target.value);
                      const option = CATEGORY_OPTIONS.find((o) => o.value === e.target.value);
                      if (option && !formDisplayName) setFormDisplayName(option.label);
                    }}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    required
                  >
                    <option value="">Select category</option>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Display Name */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Display Name</label>
                <input
                  value={formDisplayName}
                  onChange={(e) => setFormDisplayName(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  placeholder="e.g., 1-1 Calls"
                  required
                />
              </div>

              {/* Reset Period */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Reset Period</label>
                <select
                  value={formResetPeriod}
                  onChange={(e) => setFormResetPeriod(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                >
                  {RESET_PERIODS.map((p) => (
                    <option key={p} value={p}>
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formIsActive}
                  onChange={(e) => setFormIsActive(e.target.checked)}
                  className="h-4 w-4 accent-[#c51369]"
                />
                <label className="text-sm text-gray-700">Active</label>
              </div>

              {/* Tiers */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Reward Tiers ({formTiers.length})
                  </label>
                  <button
                    type="button"
                    onClick={addTier}
                    className="flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200"
                  >
                    <IoMdAdd /> Add Tier
                  </button>
                </div>

                {formTiers.length === 0 && (
                  <p className="rounded-lg bg-gray-50 p-3 text-center text-xs text-gray-400">
                    No tiers defined. This is valid — activity will be tracked without milestones.
                  </p>
                )}

                <div className="space-y-3">
                  {formTiers.map((tier, idx) => (
                    <div key={idx} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-500">TIER {tier.tier}</span>
                        <button
                          type="button"
                          onClick={() => removeTier(idx)}
                          className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <MdClose />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-gray-500">Threshold</label>
                          <input
                            type="number"
                            min="1"
                            value={tier.threshold}
                            onChange={(e) => updateTier(idx, "threshold", parseInt(e.target.value) || 0)}
                            className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Reward Type</label>
                          <select
                            value={tier.rewardType}
                            onChange={(e) => updateTier(idx, "rewardType", e.target.value)}
                            className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
                          >
                            {REWARD_TYPES.map((r) => (
                              <option key={r} value={r}>
                                {r.charAt(0).toUpperCase() + r.slice(1)}
                              </option>
                            ))}
                          </select>
                        </div>
                        {tier.rewardType === "coins" && (
                          <div>
                            <label className="text-xs text-gray-500">Coin Amount</label>
                            <input
                              type="number"
                              min="0"
                              value={tier.rewardAmount}
                              onChange={(e) => updateTier(idx, "rewardAmount", parseInt(e.target.value) || 0)}
                              className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
                            />
                          </div>
                        )}
                        {tier.rewardType === "badge" && (
                          <div>
                            <label className="text-xs text-gray-500">Badge Name</label>
                            <input
                              value={tier.badgeName}
                              onChange={(e) => updateTier(idx, "badgeName", e.target.value)}
                              className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
                              placeholder="e.g., Super Caller"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 rounded-xl bg-[#c51369] py-2.5 text-sm font-medium text-white hover:bg-pink-700"
                >
                  {editingConfig ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MilestoneConfigPage;
